import * as React from 'react'
// components
import Lookup from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup'
import ScheduledField from 'components/PageComponents/Risks/Edit/components/ScheduledField'
import { MultiValueLabel } from 'components/FormElements'
import { ReduxEntitySelect, ReduxInput, ReduxSelect } from 'components/FormElements/ReduxForm'
// constants
import {
  ACTIONS_METATAGS,
  AUTOMATION_ACTION_OBJECT_OPTIONS,
  AUTOMATION_CONDITION_ACTION_OPTIONS,
  AUTOMATION_KPI_OBJECT_OPTIONS,
  AUTOMATION_OBJECTIVE_OBJECT_OPTIONS,
  AUTOMATION_PLAN_OBJECT_OPTIONS,
  CONDITION_ACTION_OPTIONS,
  CONDITION_DATE_ACTION_OPTIONS,
  CONDITION_NULL_OPTIONS,
  CONDITION_TYPE_OPTIONS,
  ENTITIES,
  FORM_MULTIPLE_OPTIONS,
  FORM_POSITION_OPTIONS,
  KPI_METATAGS,
  NO_VALUE_FIELD_TYPES,
  OBJECT_ACTIONS_EXTENDED_OPTIONS,
  OBJECT_ACTIONS_OPTIONS,
  REVIEW_OPTIONS,
  OBJECTIVE_METATAGS,
  OBJECTS_OPTIONS,
  PARENT_RISK_TEMPLATE_METATAGS,
  PLAN_METATAGS,
  PROCESS_TYPE_OPTIONS,
  RECIPIENT_ENTITY_TYPE,
  RECIPIENT_TYPE_OPTIONS,
  RISK_TEMPLATE_METATAGS,
  RISK_TRANSACTION_TYPE_OPTIONS,
  SELECT_GROUP_LABELS,
  SWITCH_FIELD_OPTIONS,
  TRANSACTION_TYPE_OPTIONS
} from 'constants/index'
import { REVIEW_STATUSES } from 'constants/review'
import { DUE_DATE_NOW_OPTION, PlusType } from 'constants/field-recurrense'
import { FORM_SELECT_SECURITY_OPTIONS } from 'constants/security'
// graphql
import { OWNED_BY_SELECT_QUERY } from 'graphql/plan/queries'
import { PLAN_TYPES_SELECT_QUERY } from 'graphql/planType/queries'
import { SYSTEM_PROCESSES_COMBINED_QUERY } from 'graphql/systemProcesses/queries'
// types
import { Forms } from 'constants/forms'
import { IOption } from 'components/FormElements/Select'
import {
  IUser,
  IField,
  IPosition,
  FieldType,
  FieldSource,
  IFieldOption,
  IRiskTemplate,
  ISystemProcess,
  IInlineFunction,
  IRecipientField,
  IResponseTypesSet,
  ISecurityProfile,
  SystemProcessType,
  SystemProcessObject,
  IReviewApproverSource,
  IGroupsSecurityAccess,
  ISystemProcessBusinessRule,
  SystemProcessConditionAction,
  SystemProcessTransactionType,
  ISystemProcessTransactionRecipient
} from 'types/graphql'
// views
import { InputWrapper } from 'components/Modals/SystemProcessModal/Form/components/views'
// lodash
import { map, pipe, find, filter, flatten } from 'lodash/fp'
import { get, omit, isNil, uniqBy, sortBy, isEmpty, orderBy } from 'lodash'
// utils
import { isUuid } from 'utils/uuid'
import { v4 as uuid } from 'uuid'
import { safeGetOr } from 'utils/fp'
import { prepareHtml } from 'utils/html'
import { generateUserName } from 'utils/user'
import { positive, required } from 'utils/validators'
import { getCaseInput, getMappingsInitialValues } from 'utils/systemProcess/injuryMaster'
import { getFormFieldValue, getOptionByValue, getOptionByValueFlat, getValueFromOption } from 'utils/form'
import {
  getFieldsList,
  getReviewFieldIds,
  getEntitySelectQuery,
  getEntitySelectOptions,
  getEntitySelectVariables
} from 'utils/fields'
import {
  planTypeProps,
  planStatusProps,
  userAndPositionProps
} from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

export const getActionOptions = (
  entityOrField: Record<string, any>,
  object: Record<string, any>,
  processType: SystemProcessType
) => {
  let options = []

  if (!entityOrField) {
    return options
  }

  const conditionOptions = CONDITION_ACTION_OPTIONS.filter((option) => {
    if (processType === SystemProcessType.Automation) {
      return option.value !== SystemProcessConditionAction.HasChanged
    }

    return option
  })

  if (entityOrField.isObject) {
    options = AUTOMATION_CONDITION_ACTION_OPTIONS
  }

  switch (entityOrField.fieldType) {
    case FieldType.Lookup:
    case FieldType.PickList:
    case FieldType.Number:
      options = conditionOptions.filter((option) => option.value !== SystemProcessConditionAction.Contains)
      break
    case FieldType.Text:
    case FieldType.Tag:
      options = conditionOptions
      break
    case FieldType.Date:
      options = conditionOptions.filter((option) => option.value === SystemProcessConditionAction.HasChanged)
      break
    case FieldType.Switch:
      options = conditionOptions.filter((option) =>
        [SystemProcessConditionAction.Is, SystemProcessConditionAction.HasChanged].includes(option.value)
      )
      break
    case FieldType.Json:
      options = conditionOptions.filter(
        (option) =>
          option.value !== SystemProcessConditionAction.Contains && option.value !== SystemProcessConditionAction.IsNot
      )
      break
  }

  // the object's own lookups (Owned By included) can only be watched for a change
  if (entityOrField.fieldType === FieldType.Lookup && !['planType', 'planStatus'].includes(entityOrField.value)) {
    options = conditionOptions.filter((option) => option.value === SystemProcessConditionAction.HasChanged)
  }

  if (entityOrField.value === 'planType' && object.value !== SystemProcessObject.Plan) {
    options = conditionOptions.filter(
      (option) =>
        option.value !== SystemProcessConditionAction.HasChanged &&
        option.value !== SystemProcessConditionAction.Contains
    )
  }

  return options
}

const AUTOMATION_OBJECT_OPTIONS = {
  [SystemProcessObject.Plan]: AUTOMATION_PLAN_OBJECT_OPTIONS,
  [SystemProcessObject.Objective]: AUTOMATION_OBJECTIVE_OBJECT_OPTIONS,
  [SystemProcessObject.Action]: AUTOMATION_ACTION_OBJECT_OPTIONS,
  [SystemProcessObject.Kpi]: AUTOMATION_KPI_OBJECT_OPTIONS
}

// listed on their own in getEntityOrFieldOptions, with dedicated labels and value fields
const SEPARATE_CONDITION_FIELDS = ['ownedByEmployeeOrPosition', 'planType', 'planStatus']

// fields is the object's full field list; the ones in reviewFieldIds belong to its reviews
export const getEntityOrFieldOptions = (
  fields: IField[],
  object: Record<string, any>,
  processType: SystemProcessType,
  reviewFieldIds: string[] = []
) => {
  let objectFields = ['isThisAMilestone', 'commencementAt', 'completionAt', 'status']

  let options = []

  if (fields && processType) {
    if (processType === SystemProcessType.Automation) {
      objectFields = ['isThisAMilestone', 'status']
    }

    // lookups only support "Has changed" (see getActionOptions), which automations don't have
    const isLookup = (field: IField) =>
      processType !== SystemProcessType.Automation &&
      field.active &&
      field.fieldType === FieldType.Lookup &&
      (field.source || []).some((src: FieldSource) => [FieldSource.Users, FieldSource.Positions].includes(src)) &&
      !SEPARATE_CONDITION_FIELDS.includes(field.name)

    options = fields
      .filter((field) => objectFields.some((item) => item === field.name) || isLookup(field))
      .map((field) => {
        return {
          name: field.name,
          label: `${object.label}${reviewFieldIds.includes(field.id) ? ' Review' : ''}: ${field.label}`,
          value: field.name,
          fieldType: field.fieldType,
          fieldOptions: (field.options || []).map(({ id, label }) => ({ value: id, label }))
        }
      })

    if (processType !== SystemProcessType.Automation) {
      options.push({ ...object, isObject: true })
      options.push({
        label: `${object.label} Review`,
        value: SystemProcessObject.Review,
        isObject: true
      })
      options.push({
        label: `${object.label}: Owned By`,
        value: 'ownedByEmployeeOrPosition',
        fieldType: FieldType.Lookup
      })
    }

    options.push({ label: 'Plan: Plan Type', value: 'planType', fieldType: FieldType.Lookup })

    if (object.value !== SystemProcessObject.Plan) {
      options.push({ label: 'Plan: Plan Status', value: 'planStatus', fieldType: FieldType.Lookup })
    }

    // of the automation options only "<Object> Review: Status" is offered for the other process types
    const automationOptions = AUTOMATION_OBJECT_OPTIONS[object.value] || []

    options = [
      ...options,
      ...(processType === SystemProcessType.Automation
        ? automationOptions
        : automationOptions.filter(({ value }) => value.endsWith('ReviewStatus')))
    ]
  }

  return sortBy(options, (option) => option.label)
}

export const getValueOptions = (entityOrField: Record<string, any>) => {
  if (!entityOrField) {
    return []
  }

  if (entityOrField.isObject) {
    if (entityOrField.value === SystemProcessObject.Review) {
      return REVIEW_OPTIONS
    }

    const isActionOrObjective =
      entityOrField.value === SystemProcessObject.Action || entityOrField.value === SystemProcessObject.Objective

    return isActionOrObjective
      ? [...OBJECT_ACTIONS_OPTIONS, ...OBJECT_ACTIONS_EXTENDED_OPTIONS]
      : OBJECT_ACTIONS_OPTIONS
  }

  if (
    entityOrField.fieldType === FieldType.Lookup ||
    entityOrField.fieldType === FieldType.PickList ||
    entityOrField.fieldType === FieldType.RiskMatrix
  ) {
    return entityOrField.fieldOptions
  }

  if (entityOrField.fieldType === FieldType.Switch) {
    return SWITCH_FIELD_OPTIONS
  }

  return []
}

export const getValueField = ({
  action,
  field,
  entityOrField,
  object,
  isFieldRequired = false
}: {
  action: SystemProcessConditionAction
  field: string
  entityOrField: Record<string, any>
  object: Record<string, any>
  isFieldRequired: boolean
}) => {
  const isDisabled = action === SystemProcessConditionAction.HasChanged

  const validate = isFieldRequired ? [required] : []

  const defaultField = (
    <ReduxSelect
      name={`${field}.value`}
      isDisabled={isDisabled}
      options={getValueOptions(entityOrField)}
      validate={validate}
    />
  )

  if (!entityOrField) {
    return defaultField
  }

  if (entityOrField.value === 'planType') {
    return (
      <ReduxEntitySelect
        isDisabled={isDisabled}
        name={`${field}.value`}
        query={PLAN_TYPES_SELECT_QUERY}
        validate={validate}
        {...planTypeProps}
      />
    )
  }

  if (
    entityOrField.value === 'planStatus' ||
    (object.value === SystemProcessObject.Plan && entityOrField.value === 'status')
  ) {
    return (
      <ReduxEntitySelect
        isOptionSelected={(value, arr) => {
          if (!value || isEmpty(arr)) {
            return
          }

          return arr.some(({ label }) => label === value.label)
        }}
        isDisabled={isDisabled}
        isMultiple
        name={`${field}.value`}
        query={PLAN_TYPES_SELECT_QUERY}
        validate={validate}
        {...planStatusProps}
      />
    )
  }

  if (entityOrField.fieldType === FieldType.Text || entityOrField.fieldType === FieldType.Tag) {
    return <ReduxInput disabled={isDisabled} name={`${field}.value`} validate={validate} />
  }

  if (entityOrField.fieldType === FieldType.Number) {
    return (
      <ReduxInput
        disabled={isDisabled}
        name={`${field}.value`}
        type={'number'}
        validate={validate}
        inputProps={{ step: 'any' }}
      />
    )
  }

  if (entityOrField.value.includes('ReviewStatus')) {
    const options = [
      { label: 'Not started', value: REVIEW_STATUSES.NOT_STARTED },
      { label: 'In progress', value: REVIEW_STATUSES.IN_PROGRESS },
      { label: 'Complete', value: REVIEW_STATUSES.COMPLETE }
    ]

    return <ReduxSelect name={`${field}.value`} isDisabled={isDisabled} options={options} validate={validate} />
  }

  if (entityOrField.fieldType === FieldType.Json || entityOrField.value === 'completionPercentage') {
    return (
      <InputWrapper>
        <ReduxInput name={`${field}.value`} type={'number'} validate={[positive, ...validate]} />
        {entityOrField.value === 'completionPercentage' ? '%' : 'days'}
      </InputWrapper>
    )
  }

  return defaultField
}

export const getMemberOrPositionLabel = (memberOrPosition: Record<string, any>) => {
  if (memberOrPosition.firstName) {
    return generateUserName(memberOrPosition)
  }

  return memberOrPosition.description || memberOrPosition.displayName
}

// the object's own fields first, then its review picklists (their values are set on the triggering review)
export const getFieldNameOptions = (fields: IField[], reviewFieldIds: string[] = []) => {
  let options = []

  if (fields) {
    options = sortBy(
      fields.filter(
        (item) =>
          item.active &&
          ([FieldType.PickList, FieldType.Date].includes(item.fieldType) || item.name === 'ownedByEmployeeOrPosition')
      ),
      (field) => reviewFieldIds.includes(field.id)
    ).map((field) => {
      return {
        name: field.name,
        label: reviewFieldIds.includes(field.id) ? `Review: ${field.label}` : field.label,
        value: field.name,
        fieldType: field.fieldType,
        fieldOptions: field.options?.map(({ id, label }) => ({
          value: id,
          label
        }))
      }
    })
  }

  return options
}

// Lookups of the reviewed object that resolve to a person, so the approver can be taken from a field.
export const getReviewApproverFieldOptions = (fields: IField[]) =>
  (fields || [])
    .filter(
      ({ active, fieldType, source }) =>
        active &&
        fieldType === FieldType.Lookup &&
        (source || []).some((src: FieldSource) => [FieldSource.Users, FieldSource.Positions].includes(src))
    )
    .map((field) => ({
      label: field.label,
      value: field.name,
      entity: RECIPIENT_ENTITY_TYPE.FIELD
    }))

const getReviewApproverSourceInput = (approver: Record<string, any>) => {
  if (!approver) {
    return null
  }

  const value = getValueFromOption(approver)

  if (approver.entity === RECIPIENT_ENTITY_TYPE.FIELD) {
    return { fieldName: value }
  }

  if (approver.entity === RECIPIENT_ENTITY_TYPE.POSITION) {
    return { positionId: value }
  }

  return { userId: value }
}

const getReviewApproverInitials = (
  source: IReviewApproverSource,
  objectEntities: Record<string, any>[],
  persons: Record<string, any>[]
) => {
  if (!source) {
    return null
  }

  if (source.fieldName) {
    const fieldOptions = getReviewApproverFieldOptions([...objectEntities.flatMap((item) => item.fields)])

    return getOptionByValue(source.fieldName, fieldOptions)
  }

  if (source.positionId) {
    return { ...getOptionByValue(source.positionId, persons), entity: RECIPIENT_ENTITY_TYPE.POSITION }
  }

  if (source.userId) {
    return { ...getOptionByValue(source.userId, persons), entity: RECIPIENT_ENTITY_TYPE.USER }
  }

  return null
}

export const getFieldValueField = (property: Record<string, any>, field: string) => {
  if (!property) {
    return <ReduxSelect label={'Property Value'} name={`${field}.fieldValue`} options={[]} />
  }

  if (property.name === 'ownedByEmployeeOrPosition') {
    return (
      <ReduxEntitySelect
        label={'Property Value'}
        query={OWNED_BY_SELECT_QUERY}
        name={`${field}.fieldValue`}
        {...omit(userAndPositionProps, ['components'])}
      />
    )
  }

  if (property.fieldType === FieldType.Date) {
    return (
      <ScheduledField
        dueDateNowOnly
        label={'Property Value'}
        field={`${field}.fieldValue`}
        form={Forms.SYSTEM_PROCESS_CREATE_FORM}
      />
    )
  }

  return <ReduxSelect label={'Property Value'} name={`${field}.fieldValue`} options={property.fieldOptions} />
}

export const getMetatagOptions = (object: SystemProcessObject) => {
  const metatags = [{ label: SELECT_GROUP_LABELS.PLAN, options: PLAN_METATAGS }]

  if (!object) {
    return []
  }

  if (object === SystemProcessObject.Plan) {
    return metatags
  }

  if (object === SystemProcessObject.Objective) {
    return [
      {
        label: SELECT_GROUP_LABELS.OBJECTIVE,
        options: OBJECTIVE_METATAGS
      },
      ...metatags
    ]
  }

  if (object === SystemProcessObject.Action) {
    return [
      {
        label: SELECT_GROUP_LABELS.ACTION,
        options: ACTIONS_METATAGS
      },
      ...metatags
    ]
  }

  if (object === SystemProcessObject.Kpi) {
    return [
      {
        label: SELECT_GROUP_LABELS.KPI,
        options: KPI_METATAGS
      },
      ...metatags
    ]
  }
}

const getTransactionRecipients = (recipients) =>
  recipients.map(({ recipient }) => {
    const value = { fieldId: getValueFromOption(recipient), fieldLinkId: recipient.fieldLinkId || null }

    switch (recipient.entity) {
      case RECIPIENT_ENTITY_TYPE.GROUP:
        return { sendTo: recipient.value }
      case RECIPIENT_ENTITY_TYPE.USER:
        return { sendToUsers: recipient.value }
      case RECIPIENT_ENTITY_TYPE.POSITION:
        return { sendToPositions: recipient.value }
      case RECIPIENT_ENTITY_TYPE.FIELD:
        return { sendToFields: value }
      case RECIPIENT_ENTITY_TYPE.FIELD_TO_PREVIOUS:
        return { sendToPreviousFields: value }
      case RECIPIENT_ENTITY_TYPE.FIELD_TO_PARENT:
        return { sendToParentFields: value }
      case RECIPIENT_ENTITY_TYPE.FIELD_TO_USER:
        return { sendToReportsToPersonByFields: value }
      case RECIPIENT_ENTITY_TYPE.FIELD_TO_POSITION:
        return { sendToReportsToPositionByFields: value }
      default:
        return {}
    }
  })

const getSetFieldDateValue = (fieldValue: { plus?: number; plusType?: string; dueDateAt?: string }) => {
  if (!fieldValue || isNil(fieldValue.plus) || !fieldValue.plusType || !fieldValue.dueDateAt) {
    return null
  }

  return {
    value: fieldValue.dueDateAt,
    option: {
      ...DUE_DATE_NOW_OPTION,
      offset: { [fieldValue.plusType.toLowerCase()]: fieldValue.plus }
    }
  }
}

export const getTransactionsInput = (transactions: Record<string, any>[]) => {
  if (!transactions) {
    return null
  }

  return transactions.map((transaction) => {
    const id = transaction.id || uuid()
    const transactionType: SystemProcessTransactionType = getValueFromOption(transaction.transactionType)

    if (transactionType === SystemProcessTransactionType.SendEmail) {
      return {
        id,
        transactionType,
        subject: transaction.subject,
        template: prepareHtml(transaction.templateSnapshot ?? transaction.template),
        recipients: getTransactionRecipients(transaction.recipients)
      }
    }

    if (transactionType === SystemProcessTransactionType.SetField) {
      const property = transaction.fieldName

      return {
        id,
        transactionType,
        fieldName: getValueFromOption(property),
        fieldValue:
          property?.fieldType === FieldType.Date
            ? getSetFieldDateValue(transaction.fieldValue)
            : { value: getValueFromOption(transaction.fieldValue), option: transaction.fieldValue }
      }
    }

    if (transactionType === SystemProcessTransactionType.ShowFunction) {
      return {
        id,
        transactionType,
        functionName: transaction.functionName,
        availableToFieldIds: transaction.availableTo?.filter((item) => item.toField).map((item) => item.value),
        availableToPositionIds: transaction.availableTo?.filter((item) => item.toPosition).map((item) => item.value),
        availableToUserIds: transaction.availableTo?.filter((item) => item.toUser).map((item) => item.value)
      }
    }

    if (transactionType === SystemProcessTransactionType.ShowInlineFunction) {
      return {
        id,
        transactionType,
        position: getValueFromOption(transaction.position),
        inlineFunctionId: transaction.inlineFunctionId || uuid(),
        fieldLinkId: getValueFromOption(transaction.fieldLinkId),
        functionName: transaction.functionName
      }
    }

    if (transactionType === SystemProcessTransactionType.ShowRelatedForm) {
      return {
        id,
        transactionType,
        relatedForms: {
          riskTemplateId: getValueFromOption(transaction.riskTemplate),
          multiple: getValueFromOption(transaction.multiple),
          position: getValueFromOption(transaction.position),
          inheritToManagers: !!transaction.inheritToManagers,
          businessRules: get(transaction, 'businessRules', []).map(({ user, securityProfile }) => {
            if (securityProfile.value === 'inherit') {
              return {
                ...(user.entity === RECIPIENT_ENTITY_TYPE.FIELD
                  ? { userOrPositionFieldId: getValueFromOption(user) }
                  : { userOrPositionId: getValueFromOption(user) }),
                inheritSecurityProfileId: true
              }
            }

            return {
              ...(user.entity === RECIPIENT_ENTITY_TYPE.FIELD
                ? { userOrPositionFieldId: getValueFromOption(user) }
                : { userOrPositionId: getValueFromOption(user) }),
              securityProfileId: getValueFromOption(securityProfile)
            }
          })
        }
      }
    }

    if (transactionType === SystemProcessTransactionType.CreateRiskRecurrence) {
      return {
        id,
        transactionType,
        riskRecurrence: {
          fieldValues: (transaction.mappedProperties ?? []).map((item) => ({
            fieldId: getValueFromOption(item.field),
            value: getFormFieldValue(item.field.field, item.value)
          })),
          recurrence: transaction.recurrence,
          riskTemplateId: getValueFromOption(transaction.riskTemplate)
        }
      }
    }

    if (transactionType === SystemProcessTransactionType.DisableRiskRecurrence) {
      return {
        id,
        transactionType,
        disableRiskRecurrence: {
          riskTemplateId: getValueFromOption(transaction.riskTemplate)
        }
      }
    }

    if (transactionType === SystemProcessTransactionType.EnableRiskRecurrence) {
      return {
        id,
        transactionType,
        enableRiskRecurrence: {
          riskTemplateId: getValueFromOption(transaction.riskTemplate)
        }
      }
    }

    if (transactionType === SystemProcessTransactionType.Confirmation) {
      return {
        id,
        transactionType,
        subject: transaction.title,
        template: transaction.message
      }
    }

    if (transactionType === SystemProcessTransactionType.ResponseMessage) {
      return {
        id,
        transactionType,
        template: transaction.message
      }
    }

    if (transactionType === SystemProcessTransactionType.CheckMandatory) {
      return {
        id,
        transactionType,
        subject: transaction.title,
        template: transaction.message
      }
    }

    if (transactionType === SystemProcessTransactionType.DisableUpdate) {
      return {
        id,
        transactionType
      }
    }

    if (transactionType === SystemProcessTransactionType.DisableDelete) {
      return {
        id,
        transactionType
      }
    }

    if (transactionType === SystemProcessTransactionType.Restrict) {
      return {
        id,
        transactionType,
        restrictFieldId: getValueFromOption(transaction.restrictFieldId)
      }
    }

    if (transactionType === SystemProcessTransactionType.EnableGroupAccess) {
      return {
        id,
        transactionType,
        groupsSecurityAccess: get(transaction, 'groupsSecurityAccess', []).map(({ user, securityProfile }) => {
          return {
            ...(user.entity === RECIPIENT_ENTITY_TYPE.POSITION
              ? { positionId: getValueFromOption(user) }
              : { userId: getValueFromOption(user) }),
            securityProfileId: getValueFromOption(securityProfile)
          }
        })
      }
    }

    if (transactionType === SystemProcessTransactionType.EnableSupervisorHierarchy) {
      return {
        id,
        transactionType,
        supervisorHierarchy: {
          fieldIds: transaction.fieldIds.map(getValueFromOption)
        }
      }
    }

    if (transactionType === SystemProcessTransactionType.RemoveGuestAccess) {
      return {
        id,
        transactionType,
        subject: transaction.successText
      }
    }

    if (transactionType === SystemProcessTransactionType.AccessFromRelatedForm) {
      return {
        id,
        transactionType,
        relatedFormAccessList: transaction.relatedFormAccessList.map((item) => ({
          riskTemplateId: getValueFromOption(item.riskTemplateId),
          securityProfileId: getValueFromOption(item.securityProfileId)
        }))
      }
    }

    if (transactionType === SystemProcessTransactionType.SetReviewApprover) {
      return {
        id,
        transactionType,
        reviewApprovers: (transaction.reviewApprovers || []).map(getReviewApproverSourceInput)
      }
    }

    // The parent variants keep the same config; the API resolves the parent from the risk
    if (
      transactionType === SystemProcessTransactionType.CreateCase ||
      transactionType === SystemProcessTransactionType.CreateParentCase
    ) {
      return {
        id,
        transactionType,
        createCase: getCaseInput(transaction)
      }
    }

    if (
      transactionType === SystemProcessTransactionType.UpdateCase ||
      transactionType === SystemProcessTransactionType.UpdateParentCase
    ) {
      return {
        id,
        transactionType,
        updateCase: getCaseInput(transaction)
      }
    }

    if (transactionType === SystemProcessTransactionType.CreateRelatedForm) {
      return {
        id,
        transactionType,
        createRelatedForm: {
          riskTemplateId: getValueFromOption(transaction.riskTemplateId),
          fieldValues: transaction.fieldValues.map((item) => ({
            fieldId: getValueFromOption(item.field),
            value: getFormFieldValue(item.field.field, item.value, true)
          }))
        }
      }
    }
  })
}

const getConditionValue = (value: string | Record<string, any> | Record<string, any>[], fieldType: FieldType) => {
  let normalizedValue = getValueFromOption(value)

  if (Array.isArray(value)) {
    normalizedValue = value.map(({ value }) => value)
  }

  return fieldType === FieldType.Json ||
    fieldType === FieldType.Number ||
    fieldType === FieldType.Text ||
    fieldType === FieldType.Date
    ? value
    : normalizedValue
}

export const getConditionsInput = (data: Record<string, any>) => {
  if (!data.conditions) {
    return null
  }

  return data.conditions.map(({ conditionType, conditions }) => ({
    conditionType: getValueFromOption(conditionType),
    conditions: conditions.map((condition) => ({
      action: getValueFromOption(condition.action),
      conditionType: getValueFromOption(get(condition, 'conditionType')),
      entityName: condition.entityOrField.isObject ? getValueFromOption(condition.entityOrField) : null,
      fieldName: condition.entityOrField.isObject
        ? null
        : condition.entityOrField.fieldLinkId
          ? condition.entityOrField.fieldId
          : condition.entityOrField.value,
      fieldType: condition.entityOrField.isObject ? null : condition.entityOrField.fieldType,
      ...(!condition.value?.isInline && {
        value: {
          value: getConditionValue(condition.value, condition.entityOrField.fieldType),
          option: condition.value
        }
      }),
      fieldLinkId: condition.entityOrField.fieldLinkId || null,
      inlineFunctionId: condition.value?.isInline ? getValueFromOption(condition.value) : null
    }))
  }))
}

// object is the selected OBJECTS_OPTIONS option
export const getConditionValueInitials = (object: IOption, field, data, planTypes, objectEntities) => {
  if (field.value === 'planStatus' || (object.value === SystemProcessObject.Plan && field.value === 'status')) {
    const fields = pipe(
      map<any, any>(({ description, fields }) => {
        const field = fields.find((field) => field.name === 'status')
        return { description, ...field }
      }),
      map(({ description, options }) =>
        options.map(({ id, label }) => ({ value: id, label: `${description} - ${label}` }))
      ),
      flatten
    )(planTypes)

    const getLabel = (value) =>
      pipe(
        find((field: any) => field.value === value),
        safeGetOr<any, any, any>('', 'label')
      )(fields)

    if (Array.isArray(data.value)) {
      return data.value.map((value) => {
        return { label: getLabel(value), value }
      })
    } else {
      return [{ label: getLabel(data.value), value: data.value }]
    }
  }

  if (field.value === 'status') {
    const fields = getFieldsList(objectEntities, object.label)

    return pipe(
      filter((option: IField) => option.name === 'status'),
      map((field) => field.options),
      flatten,
      find((option) => option.id === data.value),
      safeGetOr<any, any, any>('', 'label'),
      (label) => ({ label, value: data.value })
    )(fields)
  }

  return data.option
}

const getRecipientInitialValue = (
  recipient: ISystemProcessTransactionRecipient,
  roles: Record<string, any>[],
  persons: Record<string, any>[],
  fields?: Record<string, any>[]
) => {
  const {
    sendTo,
    sendToUsers,
    sendToFieldIds,
    sendToPositions,
    sendToParentFieldIds,
    sendToPreviousFieldIds,
    sendToReportsToPersonByFieldIds,
    sendToReportsToPositionByFieldIds,
    sendToFields,
    sendToParentFields,
    sendToPreviousFields,
    sendToReportsToPersonByFields,
    sendToReportsToPositionByFields
  } = recipient

  if (sendTo) {
    return getOptionByValue(sendTo, roles)
  }

  if (sendToUsers?.length) {
    return {
      ...getOptionByValue(sendToUsers[0], persons),
      entity: RECIPIENT_ENTITY_TYPE.USER
    }
  }

  if (sendToPositions?.length) {
    return {
      ...getOptionByValue(sendToPositions[0], persons),
      entity: RECIPIENT_ENTITY_TYPE.POSITION
    }
  }

  const fieldConfigs = [
    {
      ids: sendToFieldIds,
      fields: sendToFields,
      entity: RECIPIENT_ENTITY_TYPE.FIELD
    },
    {
      ids: sendToParentFieldIds,
      fields: sendToParentFields,
      entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_PARENT
    },
    {
      ids: sendToReportsToPersonByFieldIds,
      fields: sendToReportsToPersonByFields,
      entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_USER,
      suffix: '(reports to person)'
    },
    {
      ids: sendToReportsToPositionByFieldIds,
      fields: sendToReportsToPositionByFields,
      entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_POSITION,
      suffix: '(reports to position)'
    },
    {
      ids: sendToPreviousFieldIds,
      fields: sendToPreviousFields,
      entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_PREVIOUS,
      suffix: '(previous)'
    }
  ]

  const getOption = (value: string | IRecipientField, options: Record<string, any>[]) => {
    const valueKey = typeof value === 'string' ? value : value.fieldId
    const linkKey = typeof value === 'string' ? undefined : value.fieldLinkId

    return options.find(
      (option) => option.value === valueKey && (option.fieldLinkId || linkKey ? option.fieldLinkId === linkKey : true)
    )
  }

  for (const { ids, fields: recipientFields, entity, suffix } of fieldConfigs) {
    if (ids?.length || recipientFields?.length) {
      const value = ids?.[0] || recipientFields?.[0]
      const option = getOption(value, fields)

      return {
        ...option,
        entity,
        fieldLinkId: recipientFields?.[0].fieldLinkId,
        ...(suffix && option ? { label: `${option.label} ${suffix}` } : {})
      }
    }
  }
}

export const getSystemProcessInitials = ({ planTypes, systemProcess, usersAndPositions, objectEntities }) => {
  if (systemProcess) {
    const object = systemProcess && getOptionByValue(systemProcess.object, OBJECTS_OPTIONS)
    const processType = get(systemProcess, 'processType')

    const fields = getFieldsList(objectEntities, object.label)

    const reviewFieldIds = getReviewFieldIds(objectEntities, object.label)

    const conditionEntityOrFieldOptions = getEntityOrFieldOptions(fields, object, processType, reviewFieldIds)
    const transactionFieldNameOptions = getFieldNameOptions(fields, reviewFieldIds)
    const userAndPositionsOptions = usersAndPositions.map((item) => ({
      ...item,
      label: getMemberOrPositionLabel(item),
      value: item.id,
      entity: item.userId ? ENTITIES.POSITION : ENTITIES.USER
    }))

    return {
      active: systemProcess.active,
      name: systemProcess.name,
      processType: getOptionByValue(systemProcess.processType, PROCESS_TYPE_OPTIONS),
      object: getOptionByValue(systemProcess.object, OBJECTS_OPTIONS),
      conditions: systemProcess.conditions.map(({ conditionType, conditions }) => ({
        conditionType: conditionType ? getOptionByValue(conditionType, CONDITION_TYPE_OPTIONS) : null,
        conditions: conditions.map((condition) => {
          const entityOrField = getOptionByValue(
            condition.fieldName || condition.entityName,
            conditionEntityOrFieldOptions
          )

          return {
            action: getOptionByValue(condition.action, [
              ...CONDITION_ACTION_OPTIONS,
              ...AUTOMATION_CONDITION_ACTION_OPTIONS
            ]),
            conditionType: getOptionByValue(condition.conditionType, CONDITION_TYPE_OPTIONS),
            entityOrField: entityOrField,
            value: getConditionValueInitials(object, entityOrField, condition.value, planTypes, objectEntities)
          }
        })
      })),
      transactions: systemProcess.transactions.map(
        ({ transactionType, subject, template, recipients, fieldName, fieldValue, reviewApprovers }) => ({
          fieldName: getOptionByValue(fieldName, transactionFieldNameOptions),
          fieldValue: get(fieldValue, 'option'),
          subject: transactionType === SystemProcessTransactionType.SendEmail ? subject : null,
          template: transactionType === SystemProcessTransactionType.SendEmail ? template : null,
          transactionType: getOptionByValue(transactionType, TRANSACTION_TYPE_OPTIONS),
          reviewApprovers:
            transactionType === SystemProcessTransactionType.SetReviewApprover
              ? (reviewApprovers || []).map((approver) =>
                  getReviewApproverInitials(approver, objectEntities, userAndPositionsOptions)
                )
              : null,
          recipients:
            transactionType === SystemProcessTransactionType.SendEmail
              ? recipients.map((recipient) => ({
                  recipient: getRecipientInitialValue(recipient, RECIPIENT_TYPE_OPTIONS, userAndPositionsOptions)
                  // recipientType: RECIPIENT_GROUPING_TYPE_OPTIONS[0]
                }))
              : null
        })
      )
    }
  }

  return { active: true, processType: PROCESS_TYPE_OPTIONS[0] }
}

export const getSystemProcessInput = (data) => ({
  active: data.active,
  name: data.name,
  processType: getValueFromOption(data.processType),
  object: getValueFromOption(data.object),
  conditions: getConditionsInput(data),
  transactions: getTransactionsInput(data.transactions)
})

export const getBusinessRulesInitialValues = (
  businessRules: ISystemProcessBusinessRule[] | IGroupsSecurityAccess[],
  usersAndPositions: (IUser | IPosition)[],
  companySecurityProfiles: ISecurityProfile[],
  riskTemplate: IRiskTemplate
) => {
  if (!businessRules) {
    return []
  }

  return businessRules.map(
    ({ userId, positionId, userOrPositionId, userOrPositionFieldId, securityProfileId, inheritSecurityProfileId }) => {
      let userOrPosition

      const id = userOrPositionId || userId || positionId

      if (id) {
        if (id === 'ALL') {
          userOrPosition = { label: 'All Users', value: 'ALL' }
        } else {
          userOrPosition = pipe(
            find<IUser | IPosition>((item) => item.id === id),
            (userOrPosition) => {
              if (userOrPosition) {
                return {
                  label: getMemberOrPositionLabel(userOrPosition),
                  value: userOrPosition.id,
                  entity: userOrPosition.hasOwnProperty('description')
                    ? RECIPIENT_ENTITY_TYPE.POSITION
                    : RECIPIENT_ENTITY_TYPE.USER
                }
              }
            }
          )(usersAndPositions)
        }
      }

      if (userOrPositionFieldId) {
        const field = riskTemplate.fields.find(({ id }) => id === userOrPositionFieldId)
        const parent = field?.parentFieldId
          ? `${riskTemplate.fields.find(({ id }) => id === field?.parentFieldId)?.label}: `
          : ''

        userOrPosition = {
          label: `${parent} ${field?.label}`,
          value: userOrPositionFieldId,
          entity: RECIPIENT_ENTITY_TYPE.FIELD
        }
      }

      const securityProfile = pipe(
        find<ISecurityProfile>(({ id }) => id === securityProfileId),
        (securityProfile) => {
          if (securityProfile) {
            return { label: securityProfile.name, value: securityProfile.id }
          }
        }
      )(companySecurityProfiles)

      return {
        user: userOrPosition,
        securityProfile: inheritSecurityProfileId
          ? {
              value: 'inherit',
              label: 'Inherit security profiles'
            }
          : securityProfile
      }
    }
  )
}

// RISK TEMPLATE
const getRTFieldMetatag = (field: IField, riskTemplate: IRiskTemplate, isParent?: boolean) => {
  const fieldLink = riskTemplate.fieldLinks.find(
    (fieldLink) => fieldLink.field.id === field.id && fieldLink.parentFieldId === field.parentFieldId
  )

  const normalizedId = `a${field.id.replace(/-/g, '_')}`

  if (field.parentFieldId) {
    const parentField = riskTemplate.fields.find(({ id }) => id === field.parentFieldId)

    const primaryFieldLink = riskTemplate.fieldLinks.find(
      (fieldLink) =>
        fieldLink.primary &&
        fieldLink.parentFieldId === field.parentFieldId &&
        fieldLink.field.entityId === field.entityId
    )

    return {
      label: `${parentField?.label}:  ${
        fieldLink
          ? fieldLink.primary
            ? fieldLink.fieldLabel
            : `${primaryFieldLink.fieldLabel} (${field.label})`
          : field.label
      }`,
      value: `{{riskTemplate.field.${normalizedId}${fieldLink ? `_${fieldLink.id.replace(/-/g, '_')}` : ''}}}`
    }
  }

  return {
    label: field.label,
    value: `{{${isParent ? 'parentRiskTemplate' : 'riskTemplate'}.field.${normalizedId}}}`
  }
}

export const getRTMetatags = (riskTemplate: IRiskTemplate, parentRiskTemplates: IRiskTemplate[]) => {
  const options = orderBy(riskTemplate.fields, ['parentFieldId', 'position'], 'desc')
    .filter(({ fieldType }) => !NO_VALUE_FIELD_TYPES.includes(fieldType))
    .map((field) => getRTFieldMetatag(field, riskTemplate))

  const uniqFieldLinks = uniqBy(riskTemplate.fieldLinks, (item) => `${item.field.entityId}-${item.field.id}`)

  const defaultMetatags = [
    { label: SELECT_GROUP_LABELS.FORM, options: RISK_TEMPLATE_METATAGS },
    { label: SELECT_GROUP_LABELS.FIELDS, options },
    ...(!!uniqFieldLinks.length
      ? [
          {
            label: SELECT_GROUP_LABELS.ANOTHER_FORM_FIELDS,
            options: uniqFieldLinks
              .map((item) => {
                const normalizedId = `a${item.field.id.replace(/-/g, '_')}`

                return [
                  {
                    label: item.field.label,
                    value: `{{riskTemplate.field.${normalizedId}}}`
                  },
                  {
                    label: `${item.field.label} (link)`,
                    value: `{{riskTemplate.field.${normalizedId}_link}}`
                  }
                ]
              })
              .flat()
          }
        ]
      : [])
  ]

  if (parentRiskTemplates?.length) {
    const parentRiskTemplatesMetatags = parentRiskTemplates.flatMap((item) => [
      {
        label: item.name.toUpperCase(),
        options: PARENT_RISK_TEMPLATE_METATAGS
      },
      {
        label: `${item.name.toUpperCase()} - FIELDS`,
        options: orderBy(item.fields, ['parentFieldId', 'position'], 'desc')
          .filter(({ fieldType }) => !NO_VALUE_FIELD_TYPES.includes(fieldType))
          .map((field) => getRTFieldMetatag(field, item, true))
      }
    ])

    return [...defaultMetatags, ...parentRiskTemplatesMetatags]
  }

  return defaultMetatags
}

const getRTFieldOptions = (field: IField, responseTypeSets: IResponseTypesSet[]) => {
  if (field.fieldType === FieldType.RiskMatrix) {
    return field.settings?.options
  }

  if (field.responseType) {
    return responseTypeSets.find((item) => item.id == field.responseTypeId)?.options
  }

  return field.options
}

export const getRTEntityOrFieldOptions = ({
  riskTemplate,
  isTransaction,
  responseTypeSets = []
}: {
  riskTemplate: IRiskTemplate
  isTransaction?: boolean
  responseTypeSets?: IResponseTypesSet[]
}) => {
  const fieldsToExclude = [FieldType.Table, FieldType.Label, FieldType.Section]

  const uniqFieldLinks = uniqBy(riskTemplate.fieldLinks, (item) => `${item.field.entityId}-${item.field.id}`)

  const fieldLinks = uniqFieldLinks.map(({ field }) => {
    const fieldOptions = (getRTFieldOptions(field, responseTypeSets) || []).map(({ id, label }) => ({
      value: id,
      label
    }))

    return {
      value: field.id,
      label: field.label,
      source: field.source,
      fieldType: field.fieldType,
      fieldOptions
    }
  })

  const fields = orderBy(riskTemplate.fields, ['parentFieldId', 'position'], 'desc')
    .filter(({ fieldType }) => !NO_VALUE_FIELD_TYPES.includes(fieldType))
    .map((field) => {
      const { id, label, fieldType, parentFieldId, entityId, source } = field

      const fieldOptions = (getRTFieldOptions(field, responseTypeSets) || []).map(({ id, label }) => ({
        value: id,
        label
      }))

      if (parentFieldId) {
        const parentField = riskTemplate.fields.find(({ id }) => id === parentFieldId)

        const fieldLink = riskTemplate.fieldLinks.find(
          (fieldLink) => fieldLink.field.id === id && fieldLink.parentFieldId === parentFieldId
        )

        const primaryFieldLink = riskTemplate.fieldLinks.find(
          (fieldLink) =>
            fieldLink.primary && fieldLink.parentFieldId === parentFieldId && fieldLink.field.entityId === entityId
        )

        return {
          source,
          fieldType,
          fieldOptions,
          label: `${parentField ? `${parentField.label}:` : ''}  ${
            fieldLink
              ? fieldLink.primary
                ? fieldLink.fieldLabel
                : `${primaryFieldLink?.fieldLabel} (${label})`
              : label
          }`,
          value: fieldLink?.id || id,
          fieldId: id,
          fieldLinkId: fieldLink?.id || null
        }
      }

      return {
        source,
        fieldType,
        fieldOptions,
        parentFieldId,
        label: label,
        value: id
      }
    })

  if (isTransaction) {
    return fields
  }

  return [
    {
      label: riskTemplate.name,
      value: SystemProcessObject.RiskTemplate,
      isObject: true
    },
    {
      label: 'Custom function triggered',
      value: 'customFunction',
      fieldType: FieldType.Lookup
    },
    {
      label: 'Created At',
      value: 'riskCreatedAt',
      fieldType: FieldType.Date
    },
    { label: SELECT_GROUP_LABELS.FIELDS, options: fields },
    { label: SELECT_GROUP_LABELS.ANOTHER_FORM_FIELDS, options: fieldLinks }
  ]
}

const pickActions = (...actions: SystemProcessConditionAction[]) =>
  CONDITION_ACTION_OPTIONS.filter((option) => actions.includes(option.value))

export const getRTActionOptions = (field: Record<string, any>, processType: SystemProcessType) => {
  if (!field) {
    return []
  }

  if (field.label === 'Custom function triggered' || field.isObject) {
    return [
      {
        label: 'Is',
        value: SystemProcessConditionAction.Is
      }
    ]
  }

  let options

  switch (field.fieldType) {
    case FieldType.Lookup:
    case FieldType.PickList:
    case FieldType.RiskMatrix:
      options = pickActions(
        SystemProcessConditionAction.Is,
        SystemProcessConditionAction.IsNot,
        SystemProcessConditionAction.HasChanged
      )
      break
    case FieldType.Number:
      options = CONDITION_ACTION_OPTIONS
      break
    case FieldType.Text:
      options = [...CONDITION_ACTION_OPTIONS, ...CONDITION_NULL_OPTIONS]
      break
    case FieldType.Date:
      options = [...CONDITION_DATE_ACTION_OPTIONS, ...CONDITION_NULL_OPTIONS]
      break
    case FieldType.Tag:
    case FieldType.DocumentOrLink:
      options = pickActions(SystemProcessConditionAction.HasChanged)
      break
    case FieldType.Switch:
      options = pickActions(SystemProcessConditionAction.Is, SystemProcessConditionAction.HasChanged)
  }

  if (processType === SystemProcessType.Automation) {
    return options.filter(({ value }) => value !== SystemProcessConditionAction.HasChanged)
  }

  return options
}

export const getRTValueField = ({
  entityOrField,
  action,
  field,
  isFieldRequired,
  objectId
}: {
  entityOrField: Record<string, any>
  action: SystemProcessConditionAction
  field: string
  isFieldRequired: boolean
  objectId: string
}) => {
  if (!entityOrField) {
    return []
  }

  const isDisabled =
    action === SystemProcessConditionAction.HasChanged ||
    action === SystemProcessConditionAction.IsNull ||
    action === SystemProcessConditionAction.IsNotNull

  const validate = isFieldRequired && !isDisabled ? [required] : []

  if (entityOrField.value === 'customFunction') {
    const getOptions = (data) => {
      const functions = get<ISystemProcess[]>(data, 'data.systemProcesses.data', [])
        .filter(
          ({ active, transactions }) =>
            active && transactions.some((item) => item.transactionType === SystemProcessTransactionType.ShowFunction)
        )
        .flatMap(({ transactions }) =>
          transactions
            .filter(({ transactionType }) => transactionType === SystemProcessTransactionType.ShowFunction)
            .map(({ functionName }) => ({
              value: functionName,
              label: functionName
            }))
        )

      const inlineFunctions = get<IInlineFunction[]>(data, 'data.getInlineFunctions', []).map((item) => ({
        label: item.functionName,
        value: item.id,
        isInline: true
      }))

      return [...inlineFunctions, ...functions]
    }

    return (
      <ReduxEntitySelect
        validate={validate}
        isDisabled={isDisabled}
        name={`${field}.value`}
        getOptions={getOptions}
        variables={{
          entityId: objectId,
          filter: {
            objectId,
            objects: [SystemProcessObject.RiskTemplate]
          }
        }}
        query={SYSTEM_PROCESSES_COMBINED_QUERY}
      />
    )
  }

  if (entityOrField.fieldType === FieldType.Text || entityOrField.fieldType === FieldType.Tag) {
    return <ReduxInput disabled={isDisabled} name={`${field}.value`} validate={validate} />
  }

  if (entityOrField.fieldType === FieldType.Number || (entityOrField.fieldType === FieldType.Date && !isDisabled)) {
    return (
      <InputWrapper>
        <ReduxInput name={`${field}.value`} type='number' validate={validate} inputProps={{ step: 'any' }} />
        {entityOrField.fieldType === FieldType.Date && 'days'}
      </InputWrapper>
    )
  }

  if (entityOrField.fieldType === FieldType.Lookup) {
    const query = getEntitySelectQuery(entityOrField.source)

    const handleSearch = ({ search, refetch }) =>
      refetch(getEntitySelectVariables({ sources: entityOrField.source, search }))

    const prepareOptions = (options: IOption[]) =>
      options.filter((option) => FORM_SELECT_SECURITY_OPTIONS.every((i) => i.value !== option.value))

    return (
      <ReduxEntitySelect
        isGrouped
        query={query}
        validate={validate}
        field={entityOrField as IField}
        name={`${field}.value`}
        isDisabled={isDisabled}
        handleSearch={handleSearch}
        prepareOptions={prepareOptions}
        getOptions={getEntitySelectOptions(entityOrField)}
        components={{
          MultiValueLabel
        }}
      />
    )
  }

  return (
    <ReduxSelect
      validate={validate}
      name={`${field}.value`}
      isDisabled={isDisabled}
      options={getValueOptions(entityOrField)}
    />
  )
}

export const getRTFieldValueField = (property: any, field: string) => {
  if (!property) {
    return <ReduxSelect label={'Property Value'} name={`${field}.fieldValue`} options={[]} isDisabled />
  }

  if (property.fieldType === FieldType.Lookup) {
    return (
      <Lookup
        menuPortalTarget={null}
        field={{ ...property, id: property.value }}
        isClearable={false}
        label={'Property Value'}
        name={`${field}.fieldValue`}
        validate={required}
      />
    )
  }

  if (property.fieldType === FieldType.Date) {
    return (
      <ScheduledField
        dueDateNowOnly
        label={'Property Value'}
        field={`${field}.fieldValue`}
        form={Forms.SYSTEM_PROCESS_CREATE_FORM}
      />
    )
  }

  return (
    <ReduxSelect
      validate={[required]}
      menuPortalTarget={null}
      label={'Property Value'}
      name={`${field}.fieldValue`}
      options={property.fieldOptions}
    />
  )
}

const findOptionLabel = (
  options: Array<IFieldOption & { relatedFieldId?: string }>,
  item: { value: any; sourceFieldId?: string; relatedFieldId?: string }
) => {
  return options.find((option) =>
    item.relatedFieldId
      ? option.value === item.value && option.relatedFieldId === item.relatedFieldId
      : option.value === (item.sourceFieldId || item.value)
  )?.label
}

const mapSelectItem = (
  options: Array<IFieldOption & { relatedFieldId?: string }>,
  item: { value: any; sourceFieldId?: string; relatedFieldId?: string }
) => ({
  label: findOptionLabel(options, item),
  value: item.value,
  ...(item.sourceFieldId && { sourceFieldId: item.sourceFieldId }),
  ...(item.relatedFieldId && { relatedFieldId: item.relatedFieldId })
})

const getMappedPropertyValueOption = (field: IField, value: any, shouldModifyDates?: boolean) => {
  switch (field.fieldType) {
    case FieldType.Lookup:
    case FieldType.PickList:
      return Array.isArray(value)
        ? value.map((item) => mapSelectItem(field.options, item))
        : mapSelectItem(field.options, value)

    case FieldType.Date: {
      if (!shouldModifyDates) {
        return value
      }

      if (!value) {
        return value
      }

      if (!value.offset) {
        // SetField's own legacy shape (before it wrapped a Date value in `{value: {...}}`) has the
        // literal directly as `value` (a string, e.g. 'DATE_NOW'), not nested under `value.value`.
        return { dueDateAt: value.value ?? value, plusType: PlusType.Days, plus: 0 }
      }

      const [plusType, amount] = Object.entries(value.offset)[0]

      return {
        dueDateAt: value.value,
        plusType: (plusType as string).toUpperCase(),
        plus: amount
      }
    }

    case FieldType.Switch:
      return { value, label: value ? 'True' : 'False' }
    default:
      return value
  }
}

const mapAvailableTo = (ids: string[], options: (IOption & any)[], additionalProps: Record<string, any>) => {
  return (ids || []).map((item) => ({
    ...getOptionByValue(item, options),
    ...additionalProps
  }))
}

export const getRTSystemProcessInitials = ({
  systemProcess,
  riskTemplate,
  usersAndPositions,
  companySecurityProfiles,
  riskTemplates,
  lookupOptions,
  parentRiskTemplatesFields,
  parentRiskTemplates = [],
  inlineFunctions,
  responseTypeSets
}: {
  systemProcess: ISystemProcess
  riskTemplate: IRiskTemplate
  usersAndPositions: (IUser | IPosition)[]
  companySecurityProfiles: ISecurityProfile[]
  riskTemplates: IRiskTemplate[]
  lookupOptions: IOption[]
  parentRiskTemplatesFields: IField[]
  parentRiskTemplates?: IRiskTemplate[]
  inlineFunctions: IInlineFunction[]
  responseTypeSets?: IResponseTypesSet[]
}) => {
  const object = {
    label: riskTemplate.name,
    value: SystemProcessObject.RiskTemplate
  }

  if (systemProcess) {
    const usersAndPositionsOptions = usersAndPositions.map((item) => ({
      ...item,
      label: getMemberOrPositionLabel(item),
      value: item.id,
      entity: 'userId' in item ? ENTITIES.POSITION : ENTITIES.USER
    }))

    const uniqFieldLinks = uniqBy(riskTemplate.fieldLinks, (item) => `${item.field.entityId}-${item.field.id}`)

    const fieldLinks = uniqFieldLinks
      .filter((item) => item.field.fieldType === FieldType.Lookup)
      .map(({ field }) => {
        return {
          value: field.id,
          label: field.label,
          entity: RECIPIENT_ENTITY_TYPE.FIELD
        }
      })

    const fieldsOptions = [...riskTemplate.fields, ...parentRiskTemplatesFields, ...fieldLinks].map(
      // @ts-ignore
      ({ id, label, entityId, parentFieldId, fieldLinkId }) => {
        const parentTemplate = riskTemplates.find((template) => template.id === entityId)

        const field = {
          label,
          fieldLinkId,
          value: id,
          entity: RECIPIENT_ENTITY_TYPE.FIELD
        }

        const parent = [...riskTemplate.fields, ...parentRiskTemplatesFields].find((f) => f.id === parentFieldId)
        const parentLabel = parent ? `${parent.label}: ` : ''

        if (parentTemplate) {
          return {
            ...field,
            label: `${parentTemplate.name} - ${parentLabel} ${label}`
          }
        }

        return { ...field, label: parentLabel ? `${parentLabel} ${field.label}` : field.label }
      }
    )

    const preparedFieldsOptions = [...fieldLinks, ...fieldsOptions]

    return {
      object,
      active: systemProcess.active,
      name: systemProcess.name,
      processType: getOptionByValue(systemProcess.processType, PROCESS_TYPE_OPTIONS),
      conditions: systemProcess.conditions.map(({ conditionType, conditions }) => ({
        conditionType: conditionType ? getOptionByValue(conditionType, CONDITION_TYPE_OPTIONS) : null,
        conditions: conditions.map((condition) => {
          const entityOrField = getOptionByValueFlat(
            condition.fieldLinkId || condition.fieldName || condition.entityName,
            getRTEntityOrFieldOptions({ riskTemplate, responseTypeSets })
          )

          return {
            action: getOptionByValue(condition.action, [
              ...CONDITION_ACTION_OPTIONS,
              ...AUTOMATION_CONDITION_ACTION_OPTIONS,
              ...CONDITION_DATE_ACTION_OPTIONS,
              ...CONDITION_NULL_OPTIONS
            ]),
            conditionType: getOptionByValue(condition.conditionType, CONDITION_TYPE_OPTIONS),
            entityOrField: entityOrField,
            value: condition.inlineFunctionId
              ? {
                  value: condition.inlineFunctionId,
                  label: inlineFunctions.find((fn) => fn.id === condition.inlineFunctionId)?.functionName,
                  isInline: true
                }
              : condition.value.option
          }
        })
      })),
      transactions: systemProcess.transactions.map((transaction) => {
        const transactionType = getOptionByValue(transaction.transactionType, RISK_TRANSACTION_TYPE_OPTIONS)

        if (transaction.transactionType === SystemProcessTransactionType.ShowFunction) {
          return {
            id: transaction.id,
            availableTo: [
              ...mapAvailableTo(transaction.availableToFieldIds, preparedFieldsOptions, { toField: true }),
              ...mapAvailableTo(transaction.availableToUserIds, usersAndPositionsOptions, { toUser: true }),
              ...mapAvailableTo(transaction.availableToPositionIds, usersAndPositionsOptions, { toPosition: true })
            ],
            functionName: transaction.functionName,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.ShowInlineFunction) {
          const fieldLink = riskTemplate.fieldLinks.find((fieldLink) => fieldLink?.id === transaction.fieldLinkId)
          const parent = riskTemplate.fields.find((field) => field.id === fieldLink?.parentFieldId)?.label
          const template = riskTemplates.find((template) => template.id === fieldLink?.field.entityId)?.name

          return {
            id: transaction.id,
            position: getOptionByValue(transaction.position, FORM_POSITION_OPTIONS),
            fieldLinkId: { value: transaction.fieldLinkId, label: `${parent}: ${template}` },
            functionName: transaction.functionName,
            inlineFunctionId: transaction.inlineFunctionId,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.ShowRelatedForm) {
          const relatedRiskTemplate = riskTemplates.find(({ id }) => id === transaction.relatedForms.riskTemplateId)

          return {
            id: transaction.id,
            multiple: getOptionByValue(transaction.relatedForms.multiple, FORM_MULTIPLE_OPTIONS),
            position: getOptionByValue(transaction.relatedForms.position, FORM_POSITION_OPTIONS),
            inheritToManagers: !!transaction.relatedForms.inheritToManagers,
            riskTemplate: { label: relatedRiskTemplate?.name, value: relatedRiskTemplate?.id },
            businessRules: getBusinessRulesInitialValues(
              transaction.relatedForms.businessRules,
              usersAndPositions,
              companySecurityProfiles,
              riskTemplate
            ),
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.SetField) {
          const fieldNameOption = getOptionByValue(
            transaction.fieldName,
            getRTEntityOrFieldOptions({ riskTemplate, responseTypeSets, isTransaction: true })
          )

          return {
            id: transaction.id,
            fieldName: fieldNameOption,
            fieldValue:
              fieldNameOption?.fieldType === FieldType.Date
                ? getMappedPropertyValueOption(fieldNameOption, transaction.fieldValue?.option, true)
                : transaction.fieldValue.option,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.SendEmail) {
          return {
            id: transaction.id,
            subject: transaction.subject,
            template: transaction.template,
            recipients: transaction.recipients.map((recipient) => ({
              recipient: getRecipientInitialValue(
                recipient,
                RECIPIENT_TYPE_OPTIONS,
                usersAndPositionsOptions,
                preparedFieldsOptions
              )
              // recipientType: RECIPIENT_GROUPING_TYPE_OPTIONS[0]
            })),
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.CreateRiskRecurrence) {
          const template = riskTemplates.find((item) => item.id === transaction.riskRecurrence.riskTemplateId)

          return {
            id: transaction.id,
            riskTemplate: { label: template.name, value: template.id },
            recurrence: transaction.riskRecurrence.recurrence,
            mappedProperties: transaction.riskRecurrence.fieldValues.map((item) => {
              const field = template.fields.find((f) => f.id === item.fieldId)
              const parent = field?.parentFieldId
                ? `${template.fields.find(({ id }) => id === field?.parentFieldId)?.label}: `
                : ''

              return {
                field: { label: `${parent} ${field.label}`, value: field.id, field },
                value: getMappedPropertyValueOption(
                  {
                    ...field,
                    options:
                      field.fieldType === FieldType.Lookup
                        ? [...lookupOptions, ...preparedFieldsOptions]
                        : field.options.map((item) => ({
                            label: item.label,
                            value: item.id
                          }))
                  },
                  item.fieldValue
                )
              }
            }),
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.DisableRiskRecurrence) {
          const template = riskTemplates.find((item) => item.id === transaction.disableRiskRecurrence?.riskTemplateId)

          return {
            id: transaction.id,
            riskTemplate: { label: template.name, value: template.id },
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.EnableRiskRecurrence) {
          const template = riskTemplates.find((item) => item.id === transaction.enableRiskRecurrence?.riskTemplateId)

          return {
            id: transaction.id,
            riskTemplate: { label: template.name, value: template.id },
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.Confirmation) {
          return {
            id: transaction.id,
            title: transaction.subject,
            message: transaction.template,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.ResponseMessage) {
          return {
            id: transaction.id,
            message: transaction.template,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.CheckMandatory) {
          return {
            id: transaction.id,
            title: transaction.subject,
            message: transaction.template,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.DisableUpdate) {
          return {
            transactionType,
            id: transaction.id
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.DisableDelete) {
          return {
            transactionType,
            id: transaction.id
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.Restrict) {
          const field = riskTemplate.fields?.find((item) => item.id === transaction.restrictFieldId)
          const parent = field?.parentFieldId
            ? `${riskTemplate.fields?.find(({ id }) => id === field?.parentFieldId)?.label}: `
            : ''

          return {
            transactionType,
            id: transaction.id,
            restrictFieldId: { label: `${parent} ${field?.label}`, value: field?.id }
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.EnableGroupAccess) {
          return {
            transactionType,
            id: transaction.id,
            groupsSecurityAccess: getBusinessRulesInitialValues(
              transaction.groupsSecurityAccess,
              usersAndPositions,
              companySecurityProfiles,
              riskTemplate
            )
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.EnableSupervisorHierarchy) {
          return {
            transactionType,
            id: transaction.id,
            fieldIds: transaction.supervisorHierarchy.fieldIds.map((fieldId) => {
              const field = riskTemplate.fields?.find((item) => item.id === fieldId)
              const parent = field?.parentFieldId
                ? `${riskTemplate.fields?.find(({ id }) => id === field?.parentFieldId)?.label}: `
                : ''

              return { label: `${parent}${field?.label}`, value: fieldId }
            })
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.RemoveGuestAccess) {
          return {
            id: transaction.id,
            successText: transaction.subject,
            transactionType
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.AccessFromRelatedForm) {
          return {
            id: transaction.id,
            transactionType,
            relatedFormAccessList: transaction.relatedFormAccessList.map((item) => {
              const securityProfile = pipe(
                find<ISecurityProfile>(({ id }) => id === item.securityProfileId),
                (item) => {
                  if (item) {
                    return { label: item.name, value: item.id }
                  }
                }
              )(companySecurityProfiles)

              const riskTemplate = pipe(
                find<IRiskTemplate>(({ id }) => id === item.riskTemplateId),
                (item) => {
                  if (item) {
                    return { label: item.name, value: item.id }
                  }
                }
              )(riskTemplates)

              return {
                riskTemplateId: riskTemplate,
                securityProfileId: securityProfile
              }
            })
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.CreateCase) {
          return {
            id: transaction.id,
            transactionType,
            propertyMappings: getMappingsInitialValues(
              transaction.createCase?.propertyMappings,
              riskTemplate.fields ?? []
            )
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.UpdateCase) {
          return {
            id: transaction.id,
            transactionType,
            propertyMappings: getMappingsInitialValues(
              transaction.updateCase?.propertyMappings,
              riskTemplate.fields ?? []
            )
          }
        }

        // The parent case types reuse createCase / updateCase, but their mappings may also point at
        // fields of the form's parent templates.
        if (transaction.transactionType === SystemProcessTransactionType.CreateParentCase) {
          return {
            id: transaction.id,
            transactionType,
            propertyMappings: getMappingsInitialValues(
              transaction.createCase?.propertyMappings,
              riskTemplate.fields ?? [],
              parentRiskTemplates
            )
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.UpdateParentCase) {
          return {
            id: transaction.id,
            transactionType,
            propertyMappings: getMappingsInitialValues(
              transaction.updateCase?.propertyMappings,
              riskTemplate.fields ?? [],
              parentRiskTemplates
            )
          }
        }

        if (transaction.transactionType === SystemProcessTransactionType.CreateRelatedForm) {
          const template = riskTemplates.find((item) => item.id === transaction.createRelatedForm.riskTemplateId)

          return {
            id: transaction.id,
            riskTemplateId: { label: template.name, value: template.id },
            fieldValues: transaction.createRelatedForm.fieldValues.map((item) => {
              const field = template.fields.find((f) => f.id === item.fieldId)

              const parent = field?.parentFieldId
                ? `${template.fields.find(({ id }) => id === field?.parentFieldId)?.label}: `
                : ''

              return {
                field: { label: `${parent} ${field.label}`, value: field.id, field },
                value: getMappedPropertyValueOption(
                  {
                    ...field,
                    options:
                      field.fieldType === FieldType.Lookup
                        ? [...lookupOptions, ...preparedFieldsOptions]
                        : field.options.map((item) => ({
                            label: item.label,
                            value: item.id
                          }))
                  },
                  item.fieldValue,
                  true
                )
              }
            }),
            transactionType
          }
        }
      })
    }
  }

  return {
    object,
    active: true,
    processType: PROCESS_TYPE_OPTIONS[0]
  }
}

export const getRTSystemProcessInput = (data: Record<string, any>, riskTemplate: IRiskTemplate) => ({
  active: data.active,
  name: data.name,
  processType: getValueFromOption(data.processType),
  object: SystemProcessObject.RiskTemplate,
  objectId: riskTemplate.id,
  conditions: data.conditions ? getConditionsInput(data) : [],
  transactions: getTransactionsInput(data.transactions)
})

export const getTemplateFieldsOptions = ({
  field,
  templates,
  parentLabel,
  isParentField
}: {
  field: IField & { fieldLinkId?: string }
  templates?: IRiskTemplate[]
  parentLabel?: string
  isParentField?: boolean
}) => {
  const fieldTemplate = templates?.find(({ id: templateId }) => templateId === field.entityId)

  const fieldLabel = fieldTemplate
    ? `${fieldTemplate.name} - ${parentLabel ? `${parentLabel}: ` : ''} ${field.label}`
    : `${parentLabel ? `${parentLabel}: ` : ''}${field.label}`

  const option = {
    label: fieldLabel,
    value: field.id,
    fieldLinkId: field.fieldLinkId,
    entity: isParentField ? RECIPIENT_ENTITY_TYPE.FIELD_TO_PARENT : RECIPIENT_ENTITY_TYPE.FIELD
  }

  if (isParentField) {
    return [option]
  }

  const additionalFields = [
    { ...option, entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_USER, label: `${fieldLabel} (reports to person)` },
    { ...option, entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_POSITION, label: `${fieldLabel} (reports to position)` },
    ...(field.historyEnabled
      ? [{ ...option, entity: RECIPIENT_ENTITY_TYPE.FIELD_TO_PREVIOUS, label: `${fieldLabel} (previous)` }]
      : [])
  ]

  return [option, ...additionalFields]
}

export const collectUserOrPositionIds = (process: ISystemProcess): string[] => {
  const ids = new Set<string>()

  process.transactions?.forEach((transaction) => {
    transaction.availableToUserIds?.forEach((id) => ids.add(id))
    transaction.availableToPositionIds?.forEach((id) => ids.add(id))

    transaction.recipients?.forEach((recipient) => {
      recipient.sendToUsers?.forEach((id) => ids.add(id))
      recipient.sendToPositions?.forEach((id) => ids.add(id))
    })

    transaction.relatedForms?.businessRules?.forEach((rule) => {
      if (rule?.userOrPositionId) {
        ids.add(rule.userOrPositionId)
      }
    })

    transaction.groupsSecurityAccess?.forEach((access) => {
      if (access?.userId) {
        ids.add(access.userId)
      }
      if (access?.positionId) {
        ids.add(access.positionId)
      }
    })

    transaction.reviewApprovers?.forEach((approver) => {
      if (approver?.userId) {
        ids.add(approver.userId)
      }
      if (approver?.positionId) {
        ids.add(approver.positionId)
      }
    })
  })

  return [...ids].filter((id) => isUuid(id))
}
